import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Icon, Spinner } from "@ebscois/react-component-library";
import { FindOnPageWrapper, TopicErrorMsg } from "./_.styled";
import FindOnPageSearchBar from "../FindOnPageSearchBar";

import useContentPreprocessors from "../../hooks/useContentPreprocessors";
import { useStore, useDispatch } from "../../store";
import { preprocessFullTopic } from "../../store/actions";
import { logFindOnPageClicked } from "../../util/analytics";
import {
  showToolbarButtons,
  hideToolbarButtons,
  getFindOnPageBtnConfig,
} from "./utils/index";

import { findOnPageModes } from "../../util/findOnPageModes";

const noop = () => null;
const CLOSE_ICON = { glyph: "close", size: "lg" };
const EMPTY_ARR = [];

const FindOnPage = ({
  content = EMPTY_ARR,
  hfcData,
  updates,
  isContentFetching,
  userExperience,
  contentColumnRef,
  toolbarDOM,
  getFullTopic,
  isContentSplit,
  isFullTopicLoading,
  isFullTopicLoaded,
  isFullTopicPreprocessing,
  fullTopicLoadingError,
  fullTopicPreprocessingError,
  findOnPageMode,
  currentSectionIndex,
  openSectionByIndex,
  openTableById,
  transformData,
  multimedia,
  tablesContent,
  referenceData,
  referenceSectionId,
  ancillaryData,
  loe,
  telemetryData,
  slugPubType,
  slugTitle,
  tab,
  pathSectionId,
  isMobile,
}) => {
  const state = useStore();
  const dispatch = useDispatch();
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [autoFocus, setAutoFocus] = useState(false);

  const isLoading =
    isFullTopicLoading || isFullTopicPreprocessing || isContentFetching;
  const errorMessage = fullTopicLoadingError || fullTopicPreprocessingError;
  const isFindInSection = findOnPageMode === findOnPageModes.findInSection;
  const openByDefault = !isMobile;
  const showButton = !openByDefault && !showSearchBox;
  const showLoader = isLoading && !showButton;
  const showInput =
    !showLoader && (isFindInSection || (!errorMessage && isFullTopicLoaded));
  const showErrorMsg = !!errorMessage && !isFindInSection;
  const showCloseButton = !openByDefault || showErrorMsg;
  const reloadOnClose = openByDefault && !!errorMessage;

  useEffect(() => {
    if (isMobile && showSearchBox) {
      hideToolbarButtons(toolbarDOM);
    } else {
      showToolbarButtons(toolbarDOM);
    }
  }, [toolbarDOM, isMobile, showSearchBox]);

  const onOpenSearchBar = useCallback(() => {
    setShowSearchBox(true);

    if (isMobile) {
      hideToolbarButtons(toolbarDOM);
    }

    setAutoFocus(true);

    if (!isFindInSection) {
      getFullTopic();
    }

    const { pageTitle: title, recordAn } = telemetryData ?? {};
    logFindOnPageClicked({ title, recordAn });
  }, [toolbarDOM, getFullTopic, telemetryData, isFindInSection, isMobile]);

  const onCloseSearchBar = useCallback(
    (reload) => {
      if (!openByDefault) {
        setShowSearchBox(false);
      }

      if (reload) {
        getFullTopic();
      }

      showToolbarButtons(toolbarDOM);
    },
    [toolbarDOM, getFullTopic, openByDefault]
  );

  const tabRef = useRef(false);
  useEffect(() => {
    if (tabRef.current !== tab) {
      onCloseSearchBar();

      //If open by default, refresh full content when switching to the default tab if no error is present. Defer auto-fetching on initial load.
      if (!tab && openByDefault && !errorMessage) {
        if (isFullTopicLoaded) {
          getFullTopic();
        } else {
          setTimeout(() => {
            getFullTopic();
          }, 1500);
        }
      }
    }

    if (openByDefault) {
      setShowSearchBox(true);
    }

    tabRef.current = tab;
  }, [
    onOpenSearchBar,
    onCloseSearchBar,
    openByDefault,
    tab,
    isMobile,
    isFullTopicLoaded,
    getFullTopic,
    errorMessage,
  ]);

  const { preProcessorInstance } = useContentPreprocessors({
    updates,
    hfcData,
    transformData,
    multimedia,
    tablesContent,
    referenceData,
    referenceSectionId,
    ancillaryData,
    userExperience,
    loe,
    slugPubType,
    slugTitle,
  });

  const findOnPageBtnConfig = getFindOnPageBtnConfig(
    userExperience,
    findOnPageMode
  );

  const [preprocessedSections, setPreprocessedSections] = useState([]);

  useEffect(() => {
    if (
      !contentColumnRef.current ||
      isContentFetching ||
      isLoading ||
      !showSearchBox
    ) {
      return;
    }

    if (isFindInSection) {
      setPreprocessedSections([contentColumnRef.current]);
      return;
    }

    const sections = preprocessFullTopic(
      dispatch,
      content,
      state,
      preProcessorInstance
    );

    if (!sections) {
      return;
    }

    setPreprocessedSections(Array.prototype.slice.call(sections));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showSearchBox,
    dispatch,
    content,
    preProcessorInstance,
    preprocessFullTopic,
    pathSectionId,
    isContentFetching,
  ]);

  const renderFindOnPageBtn = () => (
    <Button
      size={"sm"}
      variant="accent"
      data-testid="find-in-topic-button"
      onClick={onOpenSearchBar}
    >
      <Icon
        icon={{
          glyph: findOnPageBtnConfig.glyph,
          color: "white",
          size: "sm",
        }}
      />
      <span>{findOnPageBtnConfig.title}</span>
    </Button>
  );

  return (
    <FindOnPageWrapper>
      {showLoader ? (
        <Spinner size="sm" />
      ) : (
        <>
          {showButton && renderFindOnPageBtn()}
          {showSearchBox && (
            <>
              {showErrorMsg && <TopicErrorMsg>{errorMessage}</TopicErrorMsg>}
              {showInput && (
                <FindOnPageSearchBar
                  autoFocus={autoFocus}
                  isContentSplit={isContentSplit}
                  preprocessedSections={preprocessedSections}
                  contentColumnRef={contentColumnRef}
                  currentSectionIndex={currentSectionIndex}
                  openSectionByIndex={
                    isFindInSection ? noop : openSectionByIndex
                  }
                  canSearchInTables={userExperience.isSearchInTablesEnabled}
                  openTableById={openTableById}
                  telemetryData={telemetryData}
                  findOnPageBtnConfig={findOnPageBtnConfig}
                  findOnPageMode={findOnPageMode}
                  isMobile={isMobile}
                />
              )}
              {showCloseButton && (
                <Button
                  onClick={() => {
                    onCloseSearchBar(reloadOnClose);
                  }}
                  aria-label="Close"
                  variant="light"
                  kind="text"
                  size="iconOnly"
                  padded={false}
                >
                  <Icon icon={CLOSE_ICON} data-testid="find-in-section-close" />
                </Button>
              )}
            </>
          )}
        </>
      )}
    </FindOnPageWrapper>
  );
};

const sectionType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  data: PropTypes.string,
});

sectionType.subSections = PropTypes.arrayOf(sectionType);

FindOnPage.propTypes = {
  userExperience: PropTypes.object,
  contentColumnRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  toolbarDOM: PropTypes.instanceOf(Element),
  isContentFetching: PropTypes.bool,
  isContentSplit: PropTypes.bool.isRequired,
  getFullTopic: PropTypes.func.isRequired,
  isFullTopicLoading: PropTypes.bool,
  fullTopicLoadingError: PropTypes.string,
  isFullTopicPreprocessing: PropTypes.bool,
  fullTopicPreprocessingError: PropTypes.string,
  currentSectionIndex: PropTypes.number.isRequired,
  openSectionByIndex: PropTypes.func.isRequired,
  openTableById: PropTypes.func.isRequired,
  content: PropTypes.arrayOf(sectionType),
  updates: PropTypes.array,
  ancillaryData: PropTypes.array,
  transformData: PropTypes.object,
  multimedia: PropTypes.shape({ playingVideoId: PropTypes.string }),
  tablesContent: PropTypes.object,
  referenceData: PropTypes.object,
  referenceSectionId: PropTypes.string,
  telemetryData: PropTypes.shape({
    pageTitle: PropTypes.string,
    recordAn: PropTypes.string,
  }).isRequired,
  slugPubType: PropTypes.string.isRequired,
  slugTitle: PropTypes.string.isRequired,
  tab: PropTypes.string.isRequired,
  pathSectionId: PropTypes.string,
  productCode: PropTypes.string.isRequired,
};

FindOnPage.displayName = "FindOnPage";

export default FindOnPage;
